import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import Select from 'react-select';

import CustomDropdown from '@components/CustomDropdown';
import { toastError } from '@components/Toast';

import CoreProduct from '@features/crm/brands/components/CoreProduct';

import { fetchBrandProfileById } from '@store/actions/BrandProfileAction';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { fetchAllCountry, fetchEnumValues } from '@services/commonService';

import ApiClient from '@lib/apiClient';

import { getNumberUnit } from '@utils/Util';

const BrandMarketStatus = () => {
	const [brandTypes, setBrandTypes] = useState([]);
	const [groups, setGroups] = useState([]);
	const [incoTerms, setIncoTerms] = useState([]);
	const [country, setCountry] = useState([]);
	const [selectedIncoTerms, setSelectedIncoTerms] = useState([]);
	const [selectedCountry, setSelectedCountry] = useState([]);
	const [brandProfile, setBrandProfile] = useState({});
	const [editableField, setEditableField] = useState(null);
	const brandProfileStore = useSelector((store) => store.brandProfileStore);
	const { id } = useParams();
	const dispatch = useDispatch();
	const coreRef = useRef();
	const customerSegment = useRef();
	const salesChannel = useRef();

	useEffect(() => {
		fetchEnumValues('info.nitex.app.brand.BrandType', setBrandTypes);
		fetchEnumValues('info.nitex.app.brand.BrandGroup', setGroups);
		fetchEnumValues('info.nitex.app.rfq.PriceType', setIncoTerms);
		fetchAllCountry().then((data) => {
			const tmpData = data?.map((item) => ({ label: item.name, value: item.id }));
			setCountry(tmpData);
		});
	}, []);

	useEffect(() => {
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [editableField]);

	const handleClickOutside = (event) => {
		if (coreRef && !coreRef.current.contains(event.target)) {
			if (editableField === 'coreProduct') {
				setEditableField(null);
			}
		}
		if (customerSegment && !customerSegment.current.contains(event.target)) {
			if (editableField === 'customerSegment') {
				setEditableField(null);
			}
		}
		if (salesChannel && !salesChannel.current.contains(event.target)) {
			if (editableField === 'salesChannel') {
				setEditableField(null);
			}
		}
	};

	useEffect(() => {
		setBrandProfile(brandProfileStore);
		if (brandProfileStore?.incoTermsList?.length > 0) {
			let tmpData = brandProfileStore?.incoTermsList?.map((item) => ({ label: item, value: item }));
			setSelectedIncoTerms(tmpData);
		}
		if (brandProfileStore?.operationalZoneList?.length > 0) {
			let tmpData = brandProfileStore?.operationalZoneList?.map((item) => ({
				label: item.name,
				value: item.id
			}));
			setSelectedCountry(tmpData);
		}
	}, [brandProfileStore]);

	const renderCoreProductSubCategory = () => {
		return brandProfile?.brandCoreProductSubcategoryList?.map((item, index) => {
			return (
				<React.Fragment key={`category_${index}`}>
					{item.productSubCategoryName}&nbsp;
					<span className="regular-12 gray_dark_02 weight-400 mx-1">
						(${item.minPrice}-${item.maxPrice})
					</span>
				</React.Fragment>
			);
		});
	};

	const handleEditableFieldName = (fieldName) => {
		setEditableField(fieldName);
	};

	const handleChange = (e) => {
		const { name, value } = e.target;
		setBrandProfile((prev) => ({
			...prev,
			[name]: value
		}));
	};

	const saveData = async (postData, closeModalAfterEdit = true) => {
		try {
			dispatch(openLoader());
			await ApiClient.PUT('updateBrandInfo', postData);
			dispatch(fetchBrandProfileById(id));
			if (closeModalAfterEdit) {
				setEditableField(null);
			}

			dispatch(closeLoader());
		} catch {
			toastError('Update failed!');
			dispatch(closeLoader());
		}
	};

	const handleSave = async (fieldName, closeModalAfterEdit) => {
		let postData = {
			id,
			[fieldName]: brandProfile[fieldName]
		};
		await saveData(postData, closeModalAfterEdit);
	};

	const onBrandTypeClick = async (data) => {
		setBrandProfile((prev) => ({
			...prev,
			type: data.value
		}));
		let postData = {
			id,
			type: data.id
		};
		await saveData(postData);
	};

	const onBrandGroupClick = async (data) => {
		setBrandProfile((prev) => ({
			...prev,
			group: data.value
		}));
		let postData = {
			id,
			group: data.id
		};
		await saveData(postData);
	};

	const onIncoTermChange = async (value) => {
		setSelectedIncoTerms(value);
	};

	const onCountryChange = async (value) => {
		setSelectedCountry(value);
	};

	const handleBlurChange = async (fieldName) => {
		let values = [];
		if (fieldName === 'incotermList') {
			values = selectedIncoTerms.map((item) => item.value);
		} else {
			values = selectedCountry.map((item) => item.value);
		}
		let postData = {
			id,
			[fieldName]: values
		};
		await saveData(postData);
	};

	return (
		<div className="tab-contents">
			<div className="d-flex all-fourth">
				<div className="one-fourth basic-information pr-5">
					<div className="mb-12" onClick={() => handleEditableFieldName('group')}>
						<p className="regular-14 gray_dark_02 mb-1">Type</p>
						<div className="transparent-select">
							<CustomDropdown
								type="addItem"
								title={''}
								isShowLabel={true}
								onItemClick={(value) => onBrandGroupClick(value)}
								items={groups?.map((item) => ({ id: item.id, value: item.label }))}
								selectedItem={brandProfile?.group || ''}
							/>
						</div>
					</div>
					<div className="mb-12" onClick={() => handleEditableFieldName('type')}>
						<p className="regular-14 gray_dark_02 mb-1">Category</p>
						<div className="transparent-select">
							<CustomDropdown
								type="addItem"
								title={''}
								isShowLabel={true}
								onItemClick={(value) => onBrandTypeClick(value)}
								items={brandTypes?.map((item) => ({ id: item.id, value: item.label }))}
								selectedItem={brandProfile?.type || ''}
							/>
						</div>
					</div>
					<div className="mb-12">
						<p className="regular-14 gray_dark_02 mb-1 hoveable-edit">
							Annual Production req.{' '}
							<img
								src={`${process.env.PUBLIC_URL}/icons/edit-supplier.svg`}
								alt="edit"
								className="edit-hover ml-1"
								onClick={() => handleEditableFieldName('annualProduction')}
							/>
						</p>

						<p className="regular-14 gray_dark_01 weight-500">
							{editableField !== 'annualProduction' && (
								<span>
									{brandProfile?.annualProduction > 0
										? ` $${getNumberUnit(brandProfile?.annualProduction)} `
										: ''}
								</span>
							)}
						</p>

						{editableField === 'annualProduction' && (
							<input
								autoFocus={true}
								type="number"
								name="annualProduction"
								value={brandProfile?.annualProduction}
								placeholder={'00'}
								onChange={handleChange}
								onBlur={() => handleSave('annualProduction')}
							/>
						)}
					</div>
					<div className="mb-12">
						<p className="regular-14 gray_dark_02 mb-1 hoveable-edit">
							Yearly revenue{' '}
							<img
								src={`${process.env.PUBLIC_URL}/icons/edit-supplier.svg`}
								alt="edit"
								className="edit-hover ml-1"
								onClick={() => handleEditableFieldName('yearlyRevenue')}
							/>
						</p>
						<p className="regular-14 gray_dark_01 weight-500">
							{editableField !== 'yearlyRevenue' && (
								<span>
									{brandProfile?.yearlyRevenue > 0
										? ` $${getNumberUnit(brandProfile?.yearlyRevenue)} `
										: ''}
								</span>
							)}
						</p>

						{editableField === 'yearlyRevenue' && (
							<input
								autoFocus={true}
								type="number"
								name="yearlyRevenue"
								value={brandProfile?.yearlyRevenue}
								placeholder={'00'}
								onChange={handleChange}
								onBlur={() => handleSave('yearlyRevenue')}
							/>
						)}
					</div>
				</div>
				<div className="one-fourth basic-information pr-5">
					<div className="mb-12">
						<p className="regular-14 gray_dark_02 mb-1 hoveable-edit">
							Market value{' '}
							<img
								src={`${process.env.PUBLIC_URL}/icons/edit-supplier.svg`}
								alt="edit"
								className="edit-hover ml-1"
								onClick={() => handleEditableFieldName('marketValue')}
							/>
						</p>

						<p className="regular-14 gray_dark_01 weight-500">
							{editableField !== 'marketValue' && (
								<span>
									{brandProfile?.marketValue > 0
										? ` $${getNumberUnit(brandProfile?.marketValue)} `
										: ''}
								</span>
							)}
						</p>

						{editableField === 'marketValue' && (
							<input
								autoFocus={true}
								type="number"
								name="marketValue"
								value={brandProfile?.marketValue}
								placeholder={'00'}
								onChange={handleChange}
								onBlur={() => handleSave('marketValue')}
							/>
						)}
					</div>
					<div className="mb-12">
						<p className="regular-14 gray_dark_02 mb-1">Inco-terms </p>
						<div className="select-component no-shadow-react-select size36">
							<Select
								onChange={onIncoTermChange}
								value={selectedIncoTerms}
								isMulti
								isClearable={false}
								options={incoTerms}
								onBlur={() => handleBlurChange('incotermList')}
							/>
						</div>
					</div>
					<div>
						<p className="regular-14 gray_dark_02 mb-1">Operational Zone</p>
						<div className="select-component operational-zone no-shadow-react-select size36">
							<Select
								onChange={onCountryChange}
								value={selectedCountry}
								isMulti
								isClearable={false}
								options={country}
								onBlur={() => handleBlurChange('operationalZoneIdList')}
							/>
						</div>
					</div>
				</div>
				<div className="one-fourth pr-5">
					<div className="mb-12 position-relative hoveable-edit" ref={customerSegment}>
						<p className="regular-14 gray_dark_02 mb-1">
							Customer Segment{' '}
							<img
								onClick={() => handleEditableFieldName('customerSegment')}
								src={`${process.env.PUBLIC_URL}/icons/edit-supplier.svg`}
								alt="edit"
								className="edit-hover ml-1"
							/>
						</p>
						<p className="regular-14 gray_dark_01 weight-500">
							{brandProfile.customerMinSegment}{' '}
							{brandProfile?.customerMinSegment && brandProfile.customerMaxSegment ? '-' : ''}{' '}
							{brandProfile.customerMaxSegment}
						</p>
						<div
							className={`customer-segment-dropdown state-dropdown p-3 bg-white shadow-24dp ${
								editableField === 'customerSegment' ? '' : 'd-none'
							}`}
						>
							<div className="d-flex">
								<input
									autoFocus={true}
									type="number"
									value={brandProfile?.customerMinSegment || ''}
									name="customerMinSegment"
									placeholder="Min segment"
									className="mr-1"
									onChange={handleChange}
									onBlur={() => handleSave('customerMinSegment', false)}
								/>
								<input
									type="number"
									value={brandProfile?.customerMaxSegment || ''}
									name="customerMaxSegment"
									placeholder="Max segment"
									className="ml-1"
									onChange={handleChange}
									onBlur={() => handleSave('customerMaxSegment', false)}
								/>
							</div>
						</div>
					</div>
					<div className="mb-12 position-relative hoveable-edit" ref={coreRef}>
						<p className="regular-14 gray_dark_02 mb-1">
							Core Product &nbsp;
							<img
								onClick={() => handleEditableFieldName('coreProduct')}
								src={`${process.env.PUBLIC_URL}/icons/edit-supplier.svg`}
								alt="edit"
								className="edit-hover ml-1"
							/>
						</p>
						<p className="regular-14 gray_dark_01 weight-500 d-flex flex-wrap align-items-center">
							{renderCoreProductSubCategory()}
						</p>
						<div
							className={`core-product-dropdown state-dropdown p-3 bg-white shadow-24dp ${
								editableField === 'coreProduct' ? '' : 'd-none'
							}`}
						>
							<CoreProduct />
						</div>
					</div>
					<div className="mb-12 position-relative hoveable-edit" ref={salesChannel}>
						<p className="regular-14 gray_dark_02 mb-1">
							Sales Channels{' '}
							<img
								onClick={() => handleEditableFieldName('salesChannel')}
								src={`${process.env.PUBLIC_URL}/icons/edit-supplier.svg`}
								alt="edit"
								className="edit-hover ml-1"
							/>
						</p>
						<p className="regular-14 gray_dark_01 weight-500">
							eCom
							<span className="regular-12 gray_dark_02 weight-400 mx-1">
								({brandProfile?.eComSalesPercentage || 0}%)
							</span>{' '}
							Outlet{' '}
							<span className="regular-12 gray_dark_02 weight-400 mx-1">
								({brandProfile?.shopSalesPercentage || 0}%)
							</span>
						</p>
						<div
							className={`sales-channel-dropdown state-dropdown p-3 bg-white shadow-24dp ${
								editableField === 'salesChannel' ? '' : 'd-none'
							}`}
						>
							<div className="d-flex align-items-center mb-12">
								<div className="title">
									<p className="regular-14 gray_dark_01 mr-3">eCom</p>
								</div>
								<div className="form-group type mb-0 mr-12">
									<input
										autoFocus={true}
										type="number"
										value={brandProfile?.eComSalesPercentage || ''}
										name="eComSalesPercentage"
										placeholder="Sales Percentage"
										className="mr-1"
										onChange={handleChange}
										onBlur={() => handleSave('eComSalesPercentage', false)}
									/>
								</div>
							</div>
							<div className="d-flex align-items-center">
								<div className="title">
									<p className="regular-14 gray_dark_01 mr-3">Shop</p>
								</div>
								<div className="form-group type mb-0 mr-12">
									<input
										type="number"
										value={brandProfile?.shopSalesPercentage || ''}
										name="shopSalesPercentage"
										placeholder="Sales Percentage"
										className="mr-1"
										onChange={handleChange}
										onBlur={() => handleSave('shopSalesPercentage', false)}
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className="one-fourth basic-information">
					<div className="mb-12">
						<p className="regular-14 gray_dark_02 mb-1 hoveable-edit">
							No of outlet around the world
							<img
								src={`${process.env.PUBLIC_URL}/icons/edit-supplier.svg`}
								alt="edit"
								className="edit-hover ml-1"
								onClick={() => handleEditableFieldName('noOfOutlet')}
							/>
						</p>

						<p className="regular-14 gray_dark_01">
							{editableField !== 'noOfOutlet' && <span>{brandProfile?.noOfOutlet}</span>}
						</p>

						{editableField === 'noOfOutlet' && (
							<input
								autoFocus={true}
								type="number"
								name="noOfOutlet"
								value={brandProfile?.noOfOutlet || ''}
								placeholder={'00'}
								onChange={handleChange}
								onBlur={() => handleSave('noOfOutlet')}
							/>
						)}
					</div>
					<div className="mb-12">
						<p className="regular-14 gray_dark_02 mb-1 hoveable-edit">
							No of style produces each year{' '}
							<img
								src={`${process.env.PUBLIC_URL}/icons/edit-supplier.svg`}
								alt="edit"
								className="edit-hover ml-1"
								onClick={() => handleEditableFieldName('yearlyProducesStyle')}
							/>
						</p>

						<p className="regular-14 gray_dark_01 weight-500">
							{editableField !== 'yearlyProducesStyle' && (
								<span>{brandProfile?.yearlyProducesStyle}</span>
							)}
						</p>

						{editableField === 'yearlyProducesStyle' && (
							<input
								autoFocus={true}
								type="number"
								name="yearlyProducesStyle"
								value={brandProfile?.yearlyProducesStyle || ''}
								placeholder={'00'}
								onChange={handleChange}
								onBlur={() => handleSave('yearlyProducesStyle')}
							/>
						)}
					</div>
					<div className="mb-12">
						<p className="regular-14 gray_dark_02 mb-1 hoveable-edit">
							Alexa ranking{' '}
							<img
								src={`${process.env.PUBLIC_URL}/icons/edit-supplier.svg`}
								alt="edit"
								className="edit-hover ml-1"
								onClick={() => handleEditableFieldName('alexaRanking')}
							/>
						</p>

						<p className="regular-14 gray_dark_01 weight-500">
							{editableField !== 'alexaRanking' && <span>{brandProfile?.alexaRanking}</span>}
						</p>

						{editableField === 'alexaRanking' && (
							<input
								autoFocus={true}
								type="number"
								name="alexaRanking"
								value={brandProfile?.alexaRanking || ''}
								placeholder={'00'}
								onChange={handleChange}
								onBlur={() => handleSave('alexaRanking')}
							/>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default BrandMarketStatus;
